import { authenticatedApi } from '../../../shared/api/apiClientUtils.ts';

export interface UserSummaryDto {
  id: string;
  fullName: string;
  email: string;
  joinedAt: string;
  status: UserStatus;
  roles: string[];
}

export interface ProfileDto {
  givenName: string;
  familyName: string;
  utrNumber?: string;
}

export interface UserDetailDto {
  id: string;
  email: string;
  profile: ProfileDto | null;
  joinedAt: string;
  status: UserStatus;
  roles: UserRoleDto[];
  phoneNumbers: UserPhoneNumberDto[];
  invitationInfo?: UserInvitationDto;
  labourGroupId?: number | null;
  labourGroupName?: string | null;
  businessDetails?: UserBusinessDetailsDto;
  paymentDetails?: UserPaymentDetailsDto | null;
  offboardingInfo?: UserOffboardingInfoDto | null;
}

export interface UserOffboardingInfoDto {
  startedAt: string;
  startedBy: string;
  completedAt?: string | null;
  completedBy?: string | null;
}

export interface UserRoleDto {
  roleId: number;
  roleName: string;
  assignedAt: string;
  permissions: string[];
}

export interface UserPhoneNumberDto {
  phoneNumber: string;
  isPrimary: boolean;
}

export interface UserInvitationDto {
  invitationId: string;
  invitedAt: string;
  accessedAt?: string;
  expiresAt: string;
  status: InvitationStatus;
  invitedByUserName: string;
}

export interface UserBusinessDetailsDto {
  isBookingWork: boolean;
  businessName?: string;
  soleTrader?: SoleTraderDetailsDto;
  limitedCompany?: LimitedCompanyDetailsDto;
  isComplete: boolean;
}

export interface SoleTraderDetailsDto {
  hmrcFullName?: string;
  utr?: string;
  nationalInsuranceNumber?: string;
}

export interface LimitedCompanyDetailsDto {
  companyName?: string;
  utr?: string;
  companyRegistrationNumber?: string;
}

export interface UserPaymentDetailsDto {
  dateOfBirth: string | null;
  bankDetails: UserBankDetailsDto | null;
  address: UserAddressDto | null;
  cscsCard: UserCscsCardDto | null;
  isComplete: boolean;
}

export interface UserBankDetailsDto {
  accountNumber: string | null;
  sortCode: string | null;
  nameOnAccount: string | null;
}

export interface UserAddressDto {
  line1: string | null;
  line2: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  country: string | null;
}

export interface UserCscsCardDto {
  cardNumber: string | null;
  expiryDate: string | null;
}

export interface OrganisationRoleDto {
  id: number;
  name: string;
  description?: string;
}

export interface UserLookupDto {
  email: string;
  givenName?: string;
  familyName?: string;
  utrNumber?: string;
  phoneNumbers: UserPhoneNumberDto[];
}

export interface UserLookupResponse {
  user: UserLookupDto | null;
  status: UserExistenceStatus;
  message?: string;
}

export enum UserExistenceStatus {
  NotFound = 'NotFound', // User doesn't exist, can create new
  ExistsNotInOrg = 'ExistsNotInOrg', // User exists but not in current org, can add
  ExistsInOrg = 'ExistsInOrg', // User already in current org, cannot add
}

export interface OnboardUserRequest {
  email: string;
  details?: OnboardUserRequestDetails;
  role: number;
  isBookingWork?: boolean;
}

export interface OnboardUserRequestDetails {
  givenName: string;
  familyName: string;
  utrNumber?: string;
  primaryPhoneNumber?: string;
}

export interface OnboardUserResponse {
  userId: string;
  invitationId: string;
}

export enum UserStatus {
  InvitationSent = 'InvitationSent',
  Active = 'Active',
  Inactive = 'Inactive',
  Offboarding = 'Offboarding',
}

export enum InvitationStatus {
  Pending = 1,
  Accessed = 2,
  Accepted = 3,
  Rejected = 4,
  Expired = 5,
  Cancelled = 6,
}

export interface PendingInviteDto {
  invitationId: string;
  invitedEmail: string;
  invitedByUserName: string;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
  hasBeenAccessed: boolean;
}

export interface GetPendingInvitesResponse {
  invites: PendingInviteDto[];
  totalCount: number;
}

export interface ResendInviteResponse {
  newInvitationId: string;
}

export interface RevokeInviteResponse {
  success: boolean;
}

export interface GetOrganisationUsersResponse {
  users: UserSummaryDto[];
}

export interface GetOrganisationUserResponse {
  user: UserDetailDto;
}

export const getOrganisationUsers =
  async (): Promise<GetOrganisationUsersResponse> => {
    const response =
      await authenticatedApi.get<GetOrganisationUsersResponse>('/api/v1/users');
    return response.data;
  };

export const getOrganisationUserDetails = async (
  userId: string
): Promise<GetOrganisationUserResponse> => {
  const response = await authenticatedApi.get<GetOrganisationUserResponse>(
    `/api/v1/users/${userId}`
  );
  return response.data;
};

export interface SearchUsersResponse {
  users: UserSummaryDto[];
  continuationToken?: string;
  totalCount: number;
  hasMoreResults: boolean;
}

export const searchUsers = async (
  query: string,
  limit?: number,
  continuationToken?: string
): Promise<SearchUsersResponse> => {
  const params: Record<string, string> = { query };
  if (limit) params.limit = limit.toString();
  if (continuationToken) params.continuationToken = continuationToken;

  const response = await authenticatedApi.get<SearchUsersResponse>(
    '/api/v1/users/search',
    { params }
  );
  return response.data;
};

export const getOrganisationRoles = async (): Promise<
  OrganisationRoleDto[]
> => {
  const response = await authenticatedApi.get<{ roles: OrganisationRoleDto[] }>(
    '/api/v1/organisations/roles'
  );
  return response.data.roles || [];
};

export const lookupUserByEmail = async (
  email: string
): Promise<UserLookupResponse> => {
  const response = await authenticatedApi.get<UserLookupResponse>(
    `/api/v1/users/lookup?email=${encodeURIComponent(email)}`
  );
  return response.data;
};

export const onboardUser = async (
  request: OnboardUserRequest
): Promise<OnboardUserResponse> => {
  const response = await authenticatedApi.post<OnboardUserResponse>(
    '/api/v1/users/onboard',
    request
  );
  return response.data;
};

export interface UpdateUserRoleRequest {
  roleId: number;
}

export interface UpdateUserRoleResponse {
  userId: string;
  roleId: number;
  roleName: string;
}

export const updateUserRole = async (
  userId: string,
  request: UpdateUserRoleRequest
): Promise<UpdateUserRoleResponse> => {
  const response = await authenticatedApi.put<UpdateUserRoleResponse>(
    `/api/v1/users/${userId}/role`,
    request
  );
  return response.data;
};

export const getPendingInvites =
  async (): Promise<GetPendingInvitesResponse> => {
    const response = await authenticatedApi.get<GetPendingInvitesResponse>(
      '/api/v1/users/invites/pending'
    );
    return response.data;
  };

export const resendInvite = async (
  invitationId: string
): Promise<ResendInviteResponse> => {
  const response = await authenticatedApi.post<ResendInviteResponse>(
    `/api/v1/users/invites/${invitationId}/resend`
  );
  return response.data;
};

export const revokeInvite = async (
  invitationId: string
): Promise<RevokeInviteResponse> => {
  const response = await authenticatedApi.post<RevokeInviteResponse>(
    `/api/v1/users/invites/${invitationId}/revoke`
  );
  return response.data;
};

// Offboarding DTOs

export interface StartOffboardRequest {
  revokeAccess: boolean;
}

export interface OffboardReadinessResponse {
  canComplete: boolean;
  blockers: OffboardBlockerDto[];
}

export interface OffboardBlockerDto {
  type: string;
  description: string;
}

// Offboarding API functions

export const startOffboard = async (
  userId: string,
  request: StartOffboardRequest
): Promise<void> => {
  await authenticatedApi.put(`/api/v1/users/${userId}/offboard/start`, request);
};

export const getOffboardReadiness = async (
  userId: string
): Promise<OffboardReadinessResponse> => {
  const response = await authenticatedApi.get<OffboardReadinessResponse>(
    `/api/v1/users/${userId}/offboard/readiness`
  );
  return response.data;
};

export const completeOffboard = async (userId: string): Promise<void> => {
  await authenticatedApi.post(`/api/v1/users/${userId}/offboard/complete`);
};

export const cancelOffboard = async (userId: string): Promise<void> => {
  await authenticatedApi.post(`/api/v1/users/${userId}/offboard/cancel`);
};
